/**
 * The `baked_assets` (snake_case, on the wire) → MaskEditor `initialLayerRefs` (camelCase, in the
 * editor) FIELD MAPPING — the reopen half of every per-template setting's round trip.
 *
 * WHY THIS IS ITS OWN MODULE. This mapping used to be a 32-line anonymous IIFE embedded in a JSX
 * prop inside `TemplateEditor.tsx`. It was not exported, not a function, and the only suite that
 * mounts `TemplateEditor` mocks `MaskEditor` away — so a mutation audit could DELETE fields from it
 * and leave the whole suite green. 19 of 22 settings could be silently dropped on reopen with no
 * test noticing, and two shipped data-loss bugs (texture-on, clear/opacity) lived at exactly this
 * hop. Extracting it makes the mapping directly testable (`layerRefs.roundtrip.test.ts`) and, more
 * importantly, makes an OMISSION a COMPILE error rather than something a reviewer has to spot.
 *
 * This module is a pure refactor of that IIFE: same fields, same pass-through values, same
 * "every key always present (possibly `undefined`)" object shape. The per-view / flat resolution
 * (which `BakedAssetRefs` to hand in) stays in `TemplateEditor`.
 */
import type { FitMode, MockupAssets, ShadingParams, VarnishParams } from "@genz/engine-bind";
import type { MaskEditorProps } from "../editor/MaskEditor.js";
import type { BakedAssetRefs } from "./bakedSync.js";

/**
 * The editor-facing layer/settings bag restored on reopen — structurally the `initialLayerRefs`
 * prop of `MaskEditor`. Declared here rather than imported because `MaskEditor` types the prop
 * inline; the JSX call site in `TemplateEditor` is what type-checks the two against each other,
 * so a divergence there is still a compile error.
 */
export interface InitialLayerRefs {
  textureUrl?: string;
  clearBgUrl?: string;
  textureBlend?: string;
  /** Saved "Texture / effect layer" enable flag (a UV-varnish layer legitimately has no image, so
   *  this can NOT be inferred from `textureUrl` — that inference was shipped bug #1). */
  textureOn?: boolean;
  /** Saved "Show the clear case under the print area" flag, from the wire's `clear_phone`. */
  clearOn?: boolean;
  opacity?: MockupAssets["opacity"];
  varnish?: VarnishParams;
  designRotation?: number;
  matteUrl?: string;
  aiEdge?: boolean;
  aiEdgeKeep?: number;
  edgeFeather?: number;
  edgeChoke?: number;
  matteStrength?: number;
  /** Which technique produced the cutout (see `BakedAssetRefs.matte_kind`). `"chroma"` makes the
   *  reopen RE-KEY the pristine pixels instead of replaying the saved grey matte. */
  matteKind?: "ai" | "chroma";
  fillFit?: FitMode;
  designAspect?: [number, number] | null;
  printShadingStrength?: number;
  shading?: ShadingParams;
  stageDespill?: boolean;
  stageRefine?: boolean;
  stageMatteStrength?: boolean;
  stageShading?: boolean;
  matteQuality?: "fast" | "quality";
  markerColor?: [number, number, number];
  markerTol?: number;
  shadedMagentaGuide?: boolean;
  protectProduct?: boolean;
  productCategory?: string;
}

/**
 * THE MAPPING TABLE — the single source of truth for which `BakedAssetRefs` field restores which
 * editor field. `null` means "deliberately NOT an editor layer setting" (the hosted pixel refs and
 * the print geometry, which the hydrator/renderer consume, never the layer panel).
 *
 * COMPILE-TIME EXHAUSTIVENESS (the durable half of the fix). Two guards, both enforced by `tsc`:
 *
 *  1. `satisfies Record<keyof BakedAssetRefs, …>` — `Record` over a finite key UNION makes every
 *     `BakedAssetRefs` key REQUIRED here. Add a field to `BakedAssetRefs` and forget it, and the
 *     build fails with "Property '<new_field>' is missing". The same `Record` also rejects a key
 *     that is NOT on `BakedAssetRefs` (excess-property check against a fully-known target), so the
 *     table can never drift stale in the other direction either. Crucially the answer may be
 *     `null` — the compiler forces a DECISION, not a particular one.
 *  2. `_LayerRefValueTypesLineUp` below — a mapped type that fails the build if a mapped pair's
 *     VALUE types don't fit (e.g. mapping `marker_tol` onto `opacity`). Key-level exhaustiveness
 *     alone would happily accept a mis-wired pair.
 *
 * `as const` keeps the literal target names so both guards can see them.
 */
export const LAYER_REF_FIELD_MAP = {
  // --- Hosted pixel refs + print geometry: consumed by the hydrator/renderer, not the editor. ---
  generated_url: null,
  shell_url: null,
  mask_url: null,
  print_mask_url: null,
  preview_url: null,
  print_quad: null,
  // --- Layer images + the texture layer's own state. -----------------------------------------
  texture_url: "textureUrl",
  clear_bg_url: "clearBgUrl",
  texture_blend: "textureBlend",
  texture_on: "textureOn",
  varnish: "varnish",
  // --- Composite / placement. ----------------------------------------------------------------
  clear_phone: "clearOn",
  opacity: "opacity",
  design_rotation: "designRotation",
  design_fill: "fillFit",
  design_aspect: "designAspect",
  // --- AI removal: matte + edge calibration. -------------------------------------------------
  matte_url: "matteUrl",
  ai_edge: "aiEdge",
  ai_edge_keep: "aiEdgeKeep",
  edge_feather_px: "edgeFeather",
  edge_choke_px: "edgeChoke",
  matte_strength: "matteStrength",
  matte_kind: "matteKind",
  matte_quality: "matteQuality",
  // --- Artwork shading + pipeline stage toggles. ---------------------------------------------
  print_shading: "printShadingStrength",
  shading: "shading",
  stage_despill: "stageDespill",
  stage_refine: "stageRefine",
  stage_matte_strength: "stageMatteStrength",
  stage_shading: "stageShading",
  // --- Marker keying / protect product / product profile (these RESHAPE the shell + print mask,
  //     so a dropped one makes the next Save upload wrong assets over the good ones). ----------
  marker_color: "markerColor",
  marker_tol: "markerTol",
  shaded_magenta_guide: "shadedMagentaGuide",
  protect_product: "protectProduct",
  product_category: "productCategory",
} as const satisfies Record<keyof BakedAssetRefs, keyof InitialLayerRefs | null>;

type FieldMap = typeof LAYER_REF_FIELD_MAP;

/** The `InitialLayerRefs` field a ref key restores, or `never` when it maps to `null`. */
type Mapped<K extends keyof BakedAssetRefs> = Extract<FieldMap[K], keyof InitialLayerRefs>;

/**
 * Ref keys whose WIRE type is deliberately WIDER than the editor field's. `design_fill` is a plain
 * `string` on the wire (so `bakedSync.ts` stays independent of the engine's `FitMode` union) but a
 * `FitMode` in the editor. The runtime is a pass-through either way — the narrowing is purely a
 * type-level assertion — so these keys, AND ONLY THESE, are exempt from the value-type check.
 * The list is itself keyed to `BakedAssetRefs`, so a typo here is a compile error too.
 */
type WidenedOnWire = Extract<"design_fill", keyof BakedAssetRefs>;

/** Every mapped pair whose value types do NOT fit. Must be empty. */
type ValueTypeMismatches = {
  [K in Exclude<keyof BakedAssetRefs, WidenedOnWire> as [Mapped<K>] extends [never]
    ? never
    : BakedAssetRefs[K] extends InitialLayerRefs[Mapped<K>]
      ? never
      : K]: "this baked_assets field's type does not fit the InitialLayerRefs field it is mapped to";
};

type AssertEmpty<T extends Record<PropertyKey, never>> = T;

/**
 * Guard #2 (see `LAYER_REF_FIELD_MAP`). Mis-wire a pair and this alias fails to satisfy
 * `AssertEmpty`, naming the offending ref key in the error. Exported only so `noUnusedLocals`
 * keeps it alive — it has no runtime or API meaning.
 */
export type _LayerRefValueTypesLineUp = AssertEmpty<ValueTypeMismatches>;

/** The prop shape this bag must satisfy. `MaskEditor` declares it inline, so it is read here. */
type MaskEditorLayerRefs = NonNullable<MaskEditorProps["initialLayerRefs"]>;

/**
 * Guard #3 — the OTHER end of the mapping. `MaskEditor`'s `initialLayerRefs` prop exists solely to
 * receive this bag, so the two must stay in lockstep:
 *
 *  - `InitialLayerRefs extends MaskEditorLayerRefs` catches a field the editor RENAMED or NARROWED
 *    (a plain JSX assignment would not: the value is a typed return, not a fresh literal, so no
 *    excess-property check fires at the call site).
 *  - `MissingFromMapping` catches a field the editor GAINED that this mapping never fills — the
 *    same silent-drop class the table guards against, one level up.
 *
 * A deliberate divergence (a prop field sourced from somewhere other than `baked_assets`) belongs
 * in `InitialLayerRefs` as an optional field with a comment, not in a widened assertion.
 */
type MissingFromMapping = {
  [K in Exclude<keyof MaskEditorLayerRefs, keyof InitialLayerRefs>]: "MaskEditor accepts this layer-ref field but nothing in LAYER_REF_FIELD_MAP produces it";
};

/** Exported only to keep `noUnusedLocals` from dropping the assertions; no runtime or API meaning. */
export type _InitialLayerRefsMatchesMaskEditor = [
  InitialLayerRefs extends MaskEditorLayerRefs ? true : "InitialLayerRefs no longer fits MaskEditor's initialLayerRefs prop",
  AssertEmpty<MissingFromMapping>,
];

/**
 * Map ONE view's cloud `baked_assets` refs into the editor's `initialLayerRefs`.
 *
 * Pure and total: every mapped field is a straight pass-through (no defaulting, no coercion), and
 * every target key is always present — `undefined` for a ref field the record never carried. That
 * matches the object literal this replaced, and matters: `MaskEditor`'s restore effect distinguishes
 * "absent" from "explicitly false/0" via `typeof`, so presence-with-undefined and absence behave
 * the same there, while the caller's `refs ? … : undefined` gate (kept in `TemplateEditor`) is what
 * separates a brand-new template from a legacy `{}` record.
 */
export function refsToInitialLayerRefs(b: BakedAssetRefs): InitialLayerRefs {
  const out: Record<string, unknown> = {};
  for (const [refKey, layerKey] of Object.entries(LAYER_REF_FIELD_MAP) as [
    keyof BakedAssetRefs,
    keyof InitialLayerRefs | null,
  ][]) {
    if (layerKey === null) continue; // not an editor layer setting — see the table
    out[layerKey] = b[refKey];
  }
  return out as InitialLayerRefs;
}
